<template>
  <div class="id-scope sched">
    <!-- page header -->
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-calendar-clock"></span></div>
      <div>
        <div class="id-h1">{{ $t('scheduleAdmin.title') }}</div>
        <div class="pg-sub">{{ $t('scheduleAdmin.subtitle') }}</div>
      </div>
    </div>

    <!-- ===== SECTION 1: course types ===== -->
    <div class="scard">
      <div class="sec-head">
        <span class="mdi mdi-shape"></span> {{ $t('scheduleAdmin.courseTypes') }}
        <span class="spacer"></span>
        <div class="show-off" @click="showOff = !showOff">
          <span class="idsw" :class="{ on: showOff }"><span class="track"></span><span class="thumb"></span></span>
          <span class="show-off-tx">{{ $t('scheduleAdmin.showDisabled') }}</span>
        </div>
      </div>
      <div class="course-bar">
        <div v-for="c in visibleCourses" :key="c.courseid" class="course-chip"
          :class="{ off: !c.enableflag, dirty: dirtyCourse(c.courseid) === 'dirty', isnew: dirtyCourse(c.courseid) === 'isnew' }"
          :style="{ '--chip': courseColor(c) }" @click="openCourse(c)">
          <span class="dot"></span>
          <span class="nm">{{ c.coursename }}</span>
          <span class="short">{{ c.course_shortname }}</span>
          <span class="chip-count">{{ classCountOf(c.courseid) }}</span>
          <span class="edit"><span class="mdi mdi-pencil"></span></span>
        </div>
        <div class="course-chip add-course" @click="openCourse(null)">
          <span class="mdi mdi-plus"></span><span class="nm add-nm">{{ $t('scheduleAdmin.addCourse') }}</span>
        </div>
      </div>
      <div class="legend">
        <span class="item"><span class="mdi mdi-cursor-default-click"></span> {{ $t('scheduleAdmin.legendClickCourse') }}</span>
        <span class="item"><span class="lg-sw" style="background:var(--c-warning)"></span> {{ $t('scheduleAdmin.legendDirty') }}</span>
        <span class="item"><span class="lg-sw" style="background:var(--c-success)"></span> {{ $t('scheduleAdmin.legendNew') }}</span>
        <span class="item"><span class="lg-sw adm"></span> {{ $t('scheduleAdmin.legendAdmin') }}</span>
      </div>
    </div>

    <!-- ===== SECTION 2: weekly calendar ===== -->
    <div class="scard">
      <div class="sec-head">
        <span class="mdi mdi-calendar-week"></span> {{ $t('scheduleAdmin.weeklyTable') }}
        <span class="spacer"></span>
        <button class="id-btn id-btn-primary id-btn-sm" @click="openClass(null)">
          <span class="mdi mdi-plus"></span> {{ $t('scheduleAdmin.addClass') }}
        </button>
      </div>
      <div class="cal-tools">
        <span class="hint"><span class="mdi mdi-gesture-tap"></span> {{ $t('scheduleAdmin.hintCreate') }}</span>
        <span class="hint"><span class="mdi mdi-arrow-all"></span> {{ $t('scheduleAdmin.hintDrag') }}</span>
        <span class="hint"><span class="mdi mdi-pencil"></span> {{ $t('scheduleAdmin.hintEdit') }}</span>
      </div>

      <div v-if="loading" class="cal-loading">
        <div v-for="n in 6" :key="n" class="id-skel skel-row"></div>
      </div>

      <div v-else class="cal">
        <div class="cal-inner" :style="{ width: layout.innerW + 'px' }">
          <div class="cal-head">
            <div class="cal-corner2" :style="{ width: GUTTER + 'px' }"></div>
            <div v-for="hh in hourMarks" :key="'h' + hh" class="thr" :style="{ left: timeToX(hh * 60) + 'px' }">{{ pad(hh) }}:00</div>
          </div>
          <div class="cal-body" :style="{ height: layout.totalH + 'px' }" @click="onBodyClick">
            <div v-for="hh in hourMarks" :key="'v' + hh" class="vline" :style="{ left: timeToX(hh * 60) + 'px' }"></div>
            <div v-for="row in layout.rows" :key="'b' + row.di" class="day-band" :class="{ weekend: row.d.weekend }"
              :style="{ top: row.top + 'px', height: row.height + 'px' }"></div>
            <div v-for="row in layout.rows" :key="'l' + row.di" class="day-label" :class="{ weekend: row.d.weekend }"
              :style="{ top: row.top + 'px', height: row.height + 'px', width: GUTTER + 'px' }">
              <span class="th">{{ row.d.th }}</span><span class="en">{{ row.d.en.slice(0, 3) }}</span>
            </div>
            <div v-for="ev in layout.events" :key="ev.uid" class="ev"
              :class="{ adminonly: ev.adminflag, dirty: ev._dirty === 'dirty', isnew: ev._dirty === 'isnew', dragging: dragUid === ev.uid }"
              :style="{ '--ev': ev._color, left: ev._left + 'px', width: ev._width + 'px', top: ev._top + 'px', height: (LANE_H - 6) + 'px' }"
              v-hint="$t('scheduleAdmin.evTitle')" @pointerdown="evDown($event, ev.uid)" @click.stop>
              <span class="rz rz-l"></span>
              <span v-if="ev.adminflag" class="adm-badge"><span class="mdi mdi-shield-account"></span></span>
              <span class="ev-t">{{ ev._short }}</span>
              <span class="ev-time"><span class="mdi mdi-clock-outline"></span>{{ ev.classtime }}</span>
              <span class="rz rz-r"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== CLASS modal ===== -->
    <id-modal v-model="classDialog" size="md" icon="mdi-calendar-plus"
      :title="classForm.uid ? $t('scheduleAdmin.editClass') : $t('scheduleAdmin.newClass')">
      <div class="modal-sec"><span class="mdi mdi-calendar-edit"></span> {{ $t('scheduleAdmin.classInfo') }}</div>
      <div class="form-grid">
        <div class="field full">
          <label>{{ $t('table.courseName') }} <span class="req">*</span></label>
          <id-select v-model="classForm.courseid" searchable :placeholder="$t('common.selectCourse')"
            :options="courseOptions"></id-select>
        </div>
        <div class="field">
          <label>{{ $t('table.classDay') }} <span class="req">*</span></label>
          <id-select v-model="classForm.dayIdx" :options="dayOptions"></id-select>
        </div>
        <div class="field">
          <label>{{ $t('table.maxStudent') }} <span class="req">*</span></label>
          <input class="id-input" type="number" min="1" v-model.number="classForm.maxperson">
        </div>
        <div class="field">
          <label>{{ $t('scheduleAdmin.startTime') }} <span class="req">*</span></label>
          <id-select v-model="classForm.start" :options="timeOptions"></id-select>
        </div>
        <div class="field">
          <label>{{ $t('scheduleAdmin.endTime') }} <span class="req">*</span></label>
          <id-select v-model="classForm.end" :options="timeOptions"></id-select>
        </div>
      </div>

      <div class="modal-sec mt"><span class="mdi mdi-calendar-range"></span> {{ $t('scheduleAdmin.validityAndVisibility') }}</div>
      <div class="form-grid">
        <div class="field">
          <label>{{ $t('scheduleAdmin.startDate') }}</label>
          <id-date v-model="classForm.startdate" :placeholder="$t('common.selectDate')"></id-date>
        </div>
        <div class="field">
          <label>{{ $t('scheduleAdmin.endDate') }}</label>
          <id-date v-model="classForm.enddate" :placeholder="$t('common.selectDate')"></id-date>
        </div>
        <div class="field full">
          <label>{{ $t('scheduleAdmin.adminOnly') }}</label>
          <div class="switch-row">
            <span class="idsw" :class="{ on: !!classForm.adminflag }" @click="classForm.adminflag = classForm.adminflag ? 0 : 1">
              <span class="track"></span><span class="thumb"></span>
            </span>
            <span class="switch-tx">{{ $t('scheduleAdmin.adminOnlyHint') }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <button v-if="classForm.uid" class="id-btn id-btn-ghost id-btn-sm foot-left" @click="deleteClassFromDialog">
          <span class="mdi mdi-trash-can-outline" style="color:var(--c-error)"></span> {{ $t('scheduleAdmin.deleteClass') }}
        </button>
        <button class="id-btn id-btn-ghost" @click="classDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!classForm.courseid || !validTime" @click="saveClassDialog">
          <span class="mdi mdi-check"></span> {{ $t('btn.ok') }}
        </button>
      </template>
    </id-modal>

    <!-- ===== COURSE modal ===== -->
    <id-modal v-model="courseDialog" size="md" icon="mdi-shape-plus"
      :title="courseForm.courseid ? $t('scheduleAdmin.editCourse') : $t('scheduleAdmin.newCourse')">
      <div class="modal-sec"><span class="mdi mdi-book-open-variant"></span> {{ $t('scheduleAdmin.courseInfo') }}</div>
      <div class="form-grid">
        <div class="field full">
          <label>{{ $t('table.courseName') }} <span class="req">*</span></label>
          <input class="id-input" v-model="courseForm.coursename" :placeholder="$t('scheduleAdmin.courseNamePh')">
        </div>
        <div class="field">
          <label>{{ $t('table.courseShortName') }}</label>
          <input class="id-input" v-model="courseForm.course_shortname" :placeholder="$t('scheduleAdmin.shortNamePh')">
        </div>
        <div class="field">
          <label>{{ $t('scheduleAdmin.refercode') }} <span class="req" v-if="!courseForm.courseid">*</span></label>
          <input class="id-input" v-model="courseForm.refercode" :disabled="!!courseForm.courseid"
            :placeholder="$t('scheduleAdmin.refercodePh')" maxlength="5">
          <div class="fhint">
            <span class="mdi" :class="courseForm.courseid ? 'mdi-lock-outline' : 'mdi-lightbulb-on-outline'"></span>
            {{ courseForm.courseid ? $t('scheduleAdmin.refercodeLocked') : $t('scheduleAdmin.refercodeHint') }}
          </div>
        </div>
        <div class="field full">
          <label>{{ $t('scheduleAdmin.courseColor') }}</label>
          <div class="color-row">
            <div v-for="col in PALETTE" :key="col" class="color-dot" :class="{ sel: courseForm.color === col }"
              :style="{ background: col }" @click="courseForm.color = col"></div>
          </div>
        </div>
        <div class="field full" v-if="courseForm.courseid">
          <label>{{ $t('scheduleAdmin.status') }}</label>
          <div class="switch-row">
            <span class="idsw" :class="{ on: !!courseForm.enableflag }" @click="courseForm.enableflag = courseForm.enableflag ? 0 : 1">
              <span class="track"></span><span class="thumb"></span>
            </span>
            <span class="switch-tx">{{ $t('scheduleAdmin.statusHint') }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <button v-if="courseForm.courseid" class="id-btn id-btn-ghost id-btn-sm foot-left" @click="deleteCourseFromDialog">
          <span class="mdi mdi-trash-can-outline" style="color:var(--c-error)"></span> {{ $t('scheduleAdmin.deleteCourse') }}
        </button>
        <button class="id-btn id-btn-ghost" @click="courseDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="!courseForm.coursename || (!courseForm.courseid && !courseForm.refercode)" @click="saveCourseDialog">
          <span class="mdi mdi-check"></span> {{ $t('btn.ok') }}
        </button>
      </template>
    </id-modal>

    <!-- ===== CONFIRM modal (delete a class / server requireConfirm) ===== -->
    <id-modal v-model="confirmDialog" size="sm" icon="mdi-alert-outline" :title="$t('scheduleAdmin.confirmTitle')" persistent>
      <div class="danger-txt">
        <span class="mdi mdi-alert-circle-outline"></span>
        <span style="white-space:pre-line">{{ confirmMsg }}</span>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="confirmDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmProceed">
          <span class="mdi mdi-check"></span> {{ $t('btn.confirm') }}
        </button>
      </template>
    </id-modal>

    <!-- ===== SAVE BAR (draft mode) ===== -->
    <transition name="sb-slide">
      <div v-if="dirtyCount > 0" class="savebar">
        <span class="sb-dot"></span>
        <span class="sb-info"><b>{{ dirtyCount }}</b> {{ $t('scheduleAdmin.unsaved') }}<span class="sb-detail">{{ dirtyDetail }}</span></span>
        <button class="id-btn id-btn-ghost id-btn-sm" :disabled="saving" @click="revertAll">
          <span class="mdi mdi-close"></span> {{ $t('scheduleAdmin.discardAll') }}
        </button>
        <button class="id-btn id-btn-primary id-btn-sm" :disabled="saving" @click="commitAll()">
          <span class="mdi mdi-content-save"></span> {{ $t('scheduleAdmin.saveToSystem') }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { weekdayLongNames } from '../../utils/locale'

const PALETTE = ['#ec4899', '#8b5cf6', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#14b8a6', '#a855f7']
const DAYS = [
  { en: 'Monday', th: 'จันทร์' },
  { en: 'Tuesday', th: 'อังคาร' },
  { en: 'Wednesday', th: 'พุธ' },
  { en: 'Thursday', th: 'พฤหัสบดี' },
  { en: 'Friday', th: 'ศุกร์' },
  { en: 'Saturday', th: 'เสาร์', weekend: true },
  { en: 'Sunday', th: 'อาทิตย์', weekend: true },
]
const CAL_START = 8, CAL_END = 21           // calendar shows 08:00–21:00
const PX_PER_HOUR = 84, PX_PER_MIN = PX_PER_HOUR / 60
const GUTTER = 88, LANE_H = 50, ROW_PAD = 7

const pad = (n) => String(n).padStart(2, '0')
const fmt = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`
const toMin = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + m }
const parseRange = (t) => { const [a, b] = String(t).split('-'); return [toMin(a), toMin(b)] }
const clone = (o) => JSON.parse(JSON.stringify(o))

export default {
  name: 'ScheduleAdmin',
  data() {
    return {
      PALETTE, GUTTER, LANE_H,
      loading: false,
      saving: false,
      showOff: false,
      courses: [],          // working state (each: courseid, coursename, course_shortname, refercode, color, enableflag)
      classes: [],          // working state (each: uid, classid?, courseid, classday, classtime, maxperson, adminflag, startdate, enddate)
      baseCourses: [],      // last-saved snapshot
      baseClasses: [],
      tempSeq: -1,          // negative temp ids for brand-new course/class
      uidSeq: 1,            // stable client key for every class row
      // class dialog
      classDialog: false,
      classForm: { uid: null, courseid: '', dayIdx: 0, start: '17:00', end: '18:00', maxperson: 12, adminflag: 0, startdate: '', enddate: '' },
      // course dialog
      courseDialog: false,
      courseForm: { courseid: null, coursename: '', course_shortname: '', refercode: '', color: PALETTE[0], enableflag: 1 },
      // confirm dialog
      confirmDialog: false,
      confirmMsg: '',
      confirmAction: null,
      // drag
      dragUid: null,
    }
  },
  computed: {
    hourMarks() { const a = []; for (let h = CAL_START; h <= CAL_END; h++) a.push(h); return a },
    visibleCourses() { return this.courses.filter((c) => this.showOff || c.enableflag) },
    courseOptions() {
      return this.courses.filter((c) => c.enableflag || c.courseid === this.classForm.courseid)
        .map((c) => ({ value: c.courseid, label: c.coursename }))
    },
    dayOptions() { const names = weekdayLongNames(); return DAYS.map((d, i) => ({ value: i, label: names[i] })) },
    timeOptions() {
      const a = []
      for (let m = CAL_START * 60; m <= CAL_END * 60; m += 15) a.push(fmt(m))   // match the calendar range (08:00–21:00)
      return a
    },
    validTime() { return toMin(this.classForm.end) > toMin(this.classForm.start) },
    // ---- draft diff ----
    courseChangeMap() {
      const base = new Map(this.baseCourses.map((c) => [c.courseid, c]))
      const map = {}
      for (const c of this.courses) {
        if (c.courseid < 0) { map[c.courseid] = 'isnew'; continue }
        const o = base.get(c.courseid)
        if (o && (o.coursename !== c.coursename || o.course_shortname !== c.course_shortname || o.color !== c.color || o.enableflag !== c.enableflag)) {
          map[c.courseid] = 'dirty'
        }
      }
      return map
    },
    classChangeMap() {
      const base = new Map(this.baseClasses.filter((c) => c.classid > 0).map((c) => [c.classid, c]))
      const map = {}          // keyed by uid
      for (const c of this.classes) {
        if (!(c.classid > 0)) { map[c.uid] = 'isnew'; continue }
        const o = base.get(c.classid)
        if (o && (o.courseid !== c.courseid || o.classday !== c.classday || o.classtime !== c.classtime ||
          Number(o.maxperson) !== Number(c.maxperson) || Number(o.adminflag) !== Number(c.adminflag) ||
          o.startdate !== c.startdate || o.enddate !== c.enddate)) {
          map[c.uid] = 'dirty'
        }
      }
      return map
    },
    deletedClassIds() {
      const cur = new Set(this.classes.filter((c) => c.classid > 0).map((c) => c.classid))
      return this.baseClasses.filter((c) => c.classid > 0 && !cur.has(c.classid)).map((c) => c.classid)
    },
    deletedCourseIds() {
      const cur = new Set(this.courses.filter((c) => c.courseid > 0).map((c) => c.courseid))
      return this.baseCourses.filter((c) => c.courseid > 0 && !cur.has(c.courseid)).map((c) => c.courseid)
    },
    dirtyCount() {
      const cc = Object.keys(this.courseChangeMap).length
      const kc = Object.keys(this.classChangeMap).length
      return cc + kc + this.deletedClassIds.length + this.deletedCourseIds.length
    },
    dirtyDetail() {
      let nNew = 0, nMod = 0
      for (const v of Object.values(this.courseChangeMap)) v === 'isnew' ? nNew++ : nMod++
      for (const v of Object.values(this.classChangeMap)) v === 'isnew' ? nNew++ : nMod++
      const nDel = this.deletedClassIds.length + this.deletedCourseIds.length
      const parts = []
      if (nNew) parts.push(this.$t('scheduleAdmin.diffNew', { n: nNew }))
      if (nMod) parts.push(this.$t('scheduleAdmin.diffEdit', { n: nMod }))
      if (nDel) parts.push(this.$t('scheduleAdmin.diffDelete', { n: nDel }))
      return parts.length ? ' · ' + parts.join(' · ') : ''
    },
    // ---- calendar layout (lane packing + positions) ----
    layout() {
      const rows = []
      const events = []
      let y = 0
      for (let di = 0; di < DAYS.length; di++) {
        const d = DAYS[di]
        const dayCls = this.classes.filter((c) => c.classday === d.en)
          .map((c) => { const [s, e] = parseRange(c.classtime); return { c, s, e } })
          .sort((a, b) => a.s - b.s || a.e - b.e)
        const laneEnd = []
        for (const item of dayCls) {
          let lane = -1
          for (let i = 0; i < laneEnd.length; i++) { if (item.s >= laneEnd[i]) { lane = i; laneEnd[i] = item.e; break } }
          if (lane === -1) { lane = laneEnd.length; laneEnd.push(item.e) }
          item.lane = lane
        }
        const lanes = Math.max(1, laneEnd.length)
        const height = lanes * LANE_H + ROW_PAD * 2
        rows.push({ di, d, top: y, height })
        for (const item of dayCls) {
          const co = this.courses.find((x) => x.courseid === item.c.courseid)
          const dirty = this.classChangeMap[item.c.uid] || null
          events.push({
            uid: item.c.uid, classid: item.c.classid, adminflag: item.c.adminflag, classtime: item.c.classtime,
            _color: this.courseColor(co), _short: co ? (co.course_shortname || co.coursename) : '?',
            _left: this.timeToX(item.s), _width: Math.max(34, (item.e - item.s) * PX_PER_MIN) - 4,
            _top: y + ROW_PAD + item.lane * LANE_H, _dirty: dirty,
          })
        }
        y += height
      }
      return { rows, events, totalH: y, innerW: GUTTER + (CAL_END - CAL_START) * PX_PER_HOUR }
    },
  },
  async created() {
    this.boundMove = this.evMove.bind(this)
    this.boundUp = this.evUp.bind(this)
    await this.verifyThenLoad()
  },
  beforeUnmount() {
    window.removeEventListener('pointermove', this.boundMove)
    window.removeEventListener('pointerup', this.boundUp)
  },
  methods: {
    pad, fmt,
    timeToX(m) { return GUTTER + (m - CAL_START * 60) * PX_PER_MIN },
    courseColor(c) { return c && c.color ? c.color : PALETTE[(c ? c.courseid : 0) % PALETTE.length] || PALETTE[0] },
    classCountOf(courseid) { return this.classes.filter((c) => c.courseid === courseid).length },
    dirtyCourse(courseid) { return this.courseChangeMap[courseid] || null },

    async verifyThenLoad() {
      try {
        const token = this.$store.getters.getToken
        await axios.get(this.baseURL + '/verifyToken', { headers: { Authorization: `Bearer ${token}` } })
      } catch (e) {
        if (e.response && e.response.status === 401) { this.$emit('onClickChangeState', 'login'); return }
      }
      await this.loadData()
    },
    async loadData() {
      this.loading = true
      try {
        const token = this.$store.getters.getToken
        const res = await axios.get(this.baseURL + '/getScheduleData', { headers: { Authorization: `Bearer ${token}` } })
        if (res.data.success) {
          this.courses = (res.data.courses || []).map((c) => ({
            courseid: c.courseid, coursename: c.coursename, course_shortname: c.course_shortname || '',
            refercode: c.refercode || '', color: c.color || null, enableflag: c.enableflag ? 1 : 0,
          }))
          this.classes = (res.data.classes || []).map((c) => ({
            uid: this.uidSeq++, classid: c.classid, courseid: c.courseid,
            classday: c.classday, classtime: c.classtime, maxperson: Number(c.maxperson) || 0,
            adminflag: c.adminflag ? 1 : 0, startdate: this.dateOnly(c.startdate), enddate: this.dateOnly(c.enddate),
          }))
          this.snapshot()
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('msg.loadFail'))
        }
      } catch (e) {
        this.$emit('onErrorHandler', (e.response && e.response.data && e.response.data.message) || e.message)
      } finally {
        this.loading = false
      }
    },
    dateOnly(v) {
      if (!v) return ''
      const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/)
      return m ? `${m[1]}-${m[2]}-${m[3]}` : ''
    },
    snapshot() { this.baseCourses = clone(this.courses); this.baseClasses = clone(this.classes) },

    // ---------- calendar interactions ----------
    onBodyClick(e) {
      if (this.suppressClick) { this.suppressClick = false; return }
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left, y = e.clientY - rect.top
      if (x < GUTTER) return
      let min = CAL_START * 60 + (x - GUTTER) / PX_PER_MIN
      min = Math.round(min / 30) * 30
      min = Math.max(CAL_START * 60, Math.min((CAL_END - 1) * 60, min))
      let dayIdx = this.layout.rows.findIndex((r) => y >= r.top && y < r.top + r.height)
      if (dayIdx < 0) dayIdx = 0
      this.openClass(null, { dayIdx, start: fmt(min) })
    },
    evDown(e, uid) {
      const c = this.classes.find((x) => x.uid === uid)
      if (!c) return
      const [s, en] = parseRange(c.classtime)
      let mode = 'move'
      if (e.target.classList.contains('rz-l')) mode = 'resize-l'
      else if (e.target.classList.contains('rz-r')) mode = 'resize-r'
      this._drag = { uid, mode, ox: e.clientX, oy: e.clientY, os: s, oe: en, dur: en - s, moved: false }
      this.dragUid = uid
      window.addEventListener('pointermove', this.boundMove)
      window.addEventListener('pointerup', this.boundUp)
      e.preventDefault()
    },
    evMove(e) {
      const dr = this._drag
      if (!dr) return
      const c = this.classes.find((x) => x.uid === dr.uid)
      if (!c) return
      const dMin = (e.clientX - dr.ox) / PX_PER_MIN
      if (Math.abs(e.clientX - dr.ox) > 3 || Math.abs(e.clientY - dr.oy) > 3) dr.moved = true   // track BOTH axes (a vertical drag changes the day)
      let s = dr.os, en = dr.oe
      if (dr.mode === 'move') { s = dr.os + dMin; en = s + dr.dur }
      else if (dr.mode === 'resize-l') s = dr.os + dMin
      else en = dr.oe + dMin
      s = Math.round(s / 15) * 15; en = Math.round(en / 15) * 15
      if (dr.mode === 'move') {
        s = Math.max(CAL_START * 60, Math.min(CAL_END * 60 - dr.dur, s)); en = s + dr.dur
        const day = this.dayFromY(e.clientY)
        if (day != null) c.classday = DAYS[day].en
      } else {
        s = Math.max(CAL_START * 60, s); en = Math.min(CAL_END * 60, en)
        if (en - s < 15) { if (dr.mode === 'resize-l') s = en - 15; else en = s + 15 }
      }
      c.classtime = `${fmt(s)}-${fmt(en)}`
    },
    dayFromY(y) {
      const bands = this.$el.querySelectorAll('.day-band')
      if (!bands.length) return null
      for (let i = 0; i < bands.length; i++) {
        const r = bands[i].getBoundingClientRect()
        if (y >= r.top && y <= r.bottom) return i
      }
      return y < bands[0].getBoundingClientRect().top ? 0 : bands.length - 1
    },
    evUp() {
      window.removeEventListener('pointermove', this.boundMove)
      window.removeEventListener('pointerup', this.boundUp)
      const dr = this._drag
      this._drag = null
      this.dragUid = null
      if (!dr) return
      if (!dr.moved) { const c = this.classes.find((x) => x.uid === dr.uid); if (c) this.openClass(c) }
      else { this.suppressClick = true; setTimeout(() => { this.suppressClick = false }, 350) }   // swallow the post-drag click; auto-clear if it never lands on the grid
    },

    // ---------- class dialog ----------
    openClass(c, prefill) {
      if (c) {
        const idx = DAYS.findIndex((d) => d.en === c.classday)
        const [s, e] = c.classtime.split('-')
        this.classForm = {
          uid: c.uid, courseid: c.courseid, dayIdx: idx < 0 ? 0 : idx, start: s, end: e,
          maxperson: c.maxperson, adminflag: c.adminflag, startdate: c.startdate, enddate: c.enddate,
        }
      } else {
        const startVal = (prefill && prefill.start) || '17:00'
        const endM = toMin(startVal) + 60
        this.classForm = {
          uid: null, courseid: this.visibleCourses[0] ? this.visibleCourses[0].courseid : '',
          dayIdx: (prefill && prefill.dayIdx != null) ? prefill.dayIdx : 0,
          start: startVal, end: fmt(endM), maxperson: 12, adminflag: 0, startdate: '', enddate: '',
        }
      }
      this.classDialog = true
    },
    saveClassDialog() {
      if (!this.classForm.courseid || !this.validTime) return
      const payload = {
        courseid: this.classForm.courseid, classday: DAYS[this.classForm.dayIdx].en,
        classtime: `${this.classForm.start}-${this.classForm.end}`,
        maxperson: Number(this.classForm.maxperson) || 1, adminflag: this.classForm.adminflag ? 1 : 0,
        startdate: this.classForm.startdate || '', enddate: this.classForm.enddate || '',
      }
      if (this.classForm.uid) {
        const c = this.classes.find((x) => x.uid === this.classForm.uid)
        if (c) Object.assign(c, payload)
      } else {
        this.classes.push({ uid: this.uidSeq++, classid: this.tempSeq--, ...payload })
      }
      this.classDialog = false
    },
    deleteClassFromDialog() {
      const uid = this.classForm.uid
      const c = this.classes.find((x) => x.uid === uid)
      if (!c) return
      const doDelete = () => { this.classes = this.classes.filter((x) => x.uid !== uid); this.classDialog = false }
      // only a persisted class with real bookings needs the scary confirm; new/local ones just drop
      if (c.classid > 0) {
        this.confirmMsg = this.$t('scheduleAdmin.confirmDeleteClass')
        this.confirmAction = doDelete
        this.confirmDialog = true
      } else {
        doDelete()
      }
    },

    // ---------- course dialog ----------
    openCourse(c) {
      if (c) {
        this.courseForm = { courseid: c.courseid, coursename: c.coursename, course_shortname: c.course_shortname,
          refercode: c.refercode, color: this.courseColor(c), enableflag: c.enableflag }
      } else {
        this.courseForm = { courseid: null, coursename: '', course_shortname: '', refercode: '',
          color: PALETTE[this.courses.length % PALETTE.length], enableflag: 1 }
      }
      this.courseDialog = true
    },
    saveCourseDialog() {
      const name = (this.courseForm.coursename || '').trim()
      if (!name) return
      if (this.courseForm.courseid) {
        const c = this.courses.find((x) => x.courseid === this.courseForm.courseid)
        if (c) {
          c.coursename = name
          c.course_shortname = (this.courseForm.course_shortname || '').trim()
          c.color = this.courseForm.color
          c.enableflag = this.courseForm.enableflag ? 1 : 0
        }
      } else {
        this.courses.push({
          courseid: this.tempSeq--, coursename: name,
          course_shortname: (this.courseForm.course_shortname || '').trim(),
          refercode: (this.courseForm.refercode || '').trim(), color: this.courseForm.color, enableflag: 1,
        })
      }
      this.courseDialog = false
    },
    deleteCourseFromDialog() {
      const cid = this.courseForm.courseid
      if (!cid) return
      // dropping the course also drops its classes from the draft; for a persisted course the
      // server archives them in one cascade (buildPayload skips their individual delete ops)
      const doDelete = () => {
        this.courses = this.courses.filter((x) => x.courseid !== cid)
        this.classes = this.classes.filter((x) => x.courseid !== cid)
        this.courseDialog = false
      }
      // warn for a persisted course — and ALSO for a local (negative temp id) draft course
      // holding persisted classes moved into it: dropping those queues REAL class deletes
      const hasPersistedClasses = this.classes.some((x) => x.courseid === cid && x.classid > 0)
      if (cid > 0 || hasPersistedClasses) {
        this.confirmMsg = this.$t('scheduleAdmin.confirmDeleteCourse')
        this.confirmAction = doDelete
        this.confirmDialog = true
      } else {
        doDelete()
      }
    },

    // ---------- confirm ----------
    confirmProceed() { const fn = this.confirmAction; this.confirmDialog = false; this.confirmAction = null; if (fn) fn() },

    // ---------- draft save / revert ----------
    revertAll() {
      this.courses = clone(this.baseCourses)
      this.classes = clone(this.baseClasses)
      this.$emit('onInfoHandler', this.$t('scheduleAdmin.reverted'))
    },
    buildPayload() {
      const baseCourse = new Map(this.baseCourses.map((c) => [c.courseid, c]))
      const courses = []
      for (const c of this.courses) {
        if (c.courseid < 0) {
          courses.push({ op: 'create', courseid: c.courseid, coursename: c.coursename, course_shortname: c.course_shortname, refercode: c.refercode, color: c.color })
        } else {
          const o = baseCourse.get(c.courseid)
          if (o && (o.coursename !== c.coursename || o.course_shortname !== c.course_shortname || o.color !== c.color || o.enableflag !== c.enableflag)) {
            courses.push({ op: 'update', courseid: c.courseid, coursename: c.coursename, course_shortname: c.course_shortname, color: c.color, enableflag: c.enableflag })
          }
        }
      }
      const baseClass = new Map(this.baseClasses.filter((c) => c.classid > 0).map((c) => [c.classid, c]))
      const classes = []
      for (const c of this.classes) {
        const body = { courseid: c.courseid, classday: c.classday, classtime: c.classtime, maxperson: c.maxperson, adminflag: c.adminflag, startdate: c.startdate || null, enddate: c.enddate || null }
        if (!(c.classid > 0)) {
          classes.push({ op: 'create', ...body })
        } else {
          const o = baseClass.get(c.classid)
          if (o && (o.courseid !== c.courseid || o.classday !== c.classday || o.classtime !== c.classtime ||
            Number(o.maxperson) !== Number(c.maxperson) || Number(o.adminflag) !== Number(c.adminflag) ||
            o.startdate !== c.startdate || o.enddate !== c.enddate)) {
            classes.push({ op: 'update', classid: c.classid, ...body })
          }
        }
      }
      // course deletes cascade to their classes server-side — skip those classes' own
      // delete ops so the pre-flight doesn't double-check (and double-report) them
      const deletedCourses = new Set(this.deletedCourseIds)
      for (const id of this.deletedCourseIds) courses.push({ op: 'delete', courseid: id })
      for (const id of this.deletedClassIds) {
        const o = baseClass.get(id)
        if (o && deletedCourses.has(o.courseid)) continue
        classes.push({ op: 'delete', classid: id })
      }
      return { courses, classes }
    },
    async commitAll(confirmed = false) {
      if (this.dirtyCount === 0 || this.saving) return
      const { courses, classes } = this.buildPayload()
      this.saving = true
      this.$emit('onLoading', true)
      try {
        const token = this.$store.getters.getToken
        const res = await axios.post(this.baseURL + '/saveScheduleBatch',
          { courses, classes, confirmed }, { headers: { Authorization: `Bearer ${token}` } })
        if (res.data.success) {
          this.$emit('onInfoHandler', this.$t('scheduleAdmin.saved'))
          await this.loadData()
        } else if (res.data.requireConfirm) {
          this.confirmMsg = res.data.message + '\n\n' + this.$t('scheduleAdmin.confirmProceedHint')
          this.confirmAction = () => this.commitAll(true)
          this.confirmDialog = true
        } else {
          this.$emit('onErrorHandler', res.data.message || this.$t('msg.updateFail'))
        }
      } catch (e) {
        this.$emit('onErrorHandler', (e.response && e.response.data && e.response.data.message) || e.message)
      } finally {
        this.saving = false
        this.$emit('onLoading', false)
      }
    },
  },
}
</script>

<style scoped>
.sched { padding-bottom: 90px; }
.sec-head { display: flex; align-items: center; gap: 8px; font-family: var(--font-head); font-weight: 700; color: var(--c-text-heading); font-size: 16px; margin-bottom: 14px; }
.sec-head > .mdi { color: var(--c-primary); font-size: 20px; }
.spacer { flex: 1; }
.show-off { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.show-off-tx { font-size: 13px; color: var(--c-text-muted); font-weight: 400; }

/* switches use the global .idsw standard (istar-pages.css) — no local override: a scoped
   variant here fought the global rules per-property and rendered a distorted hybrid */
.switch-row { display: flex; align-items: center; gap: 10px; height: 44px; }
.switch-tx { font-size: 13px; color: var(--c-text-muted); }

/* course chips */
.course-bar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.course-chip { display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 8px 0 14px; border-radius: var(--radius-pill); background: var(--c-surface); border: 1.5px solid var(--chip); cursor: pointer; transition: all .16s; }
.course-chip:hover { box-shadow: var(--shadow-sm); transform: translateY(-1px); }
.course-chip .dot { width: 14px; height: 14px; border-radius: 50%; background: var(--chip); flex: none; }
.course-chip .nm { font-weight: 700; font-family: var(--font-head); color: var(--c-text-heading); font-size: 14px; }
.course-chip .short { font-size: 12px; color: var(--c-text-muted); }
.course-chip .edit { width: 26px; height: 26px; border-radius: 8px; display: grid; place-items: center; color: var(--c-text-muted); background: var(--c-surface-2); }
.course-chip .edit .mdi { font-size: 15px; }
.course-chip.off { opacity: .55; border-style: dashed; }
.course-chip.off .nm { text-decoration: line-through; }
.course-chip.dirty { box-shadow: 0 0 0 2.5px var(--c-warning); }
.course-chip.isnew { box-shadow: 0 0 0 2.5px var(--c-success); }
.chip-count { background: var(--chip); color: #fff; font-size: 11px; font-weight: 700; border-radius: var(--radius-pill); padding: 1px 7px; min-width: 20px; text-align: center; }
.add-course { border-style: dashed; border-color: var(--c-border-strong); background: var(--c-surface-2); }
.add-course .mdi { color: var(--c-primary); font-size: 18px; }
.add-course .add-nm { color: var(--c-primary-dark); }

/* legend */
.legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: var(--c-text-muted); margin-top: 12px; align-items: center; }
.legend .item { display: inline-flex; align-items: center; gap: 6px; }
.legend .mdi { color: var(--c-primary); font-size: 15px; }
.legend .lg-sw { width: 13px; height: 13px; border-radius: 4px; }
.legend .lg-sw.adm { background: #c98aad; background-image: repeating-linear-gradient(45deg, rgba(255,255,255,.5) 0 3px, transparent 3px 6px); }

/* calendar toolbar */
.cal-tools { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.hint { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--c-text-muted); background: var(--c-surface-2); border: 1px dashed var(--c-border-strong); border-radius: var(--radius-pill); padding: 5px 12px; }
.hint .mdi { color: var(--c-primary); font-size: 15px; }

/* calendar (horizontal: time X, days Y) */
.cal-loading { display: flex; flex-direction: column; gap: 8px; }
.skel-row { height: 44px; border-radius: 10px; }
.cal { border: 1px solid var(--c-border); border-radius: var(--radius-md); overflow-x: auto; overflow-y: hidden; background: var(--c-surface); }
.cal-inner { position: relative; }
.cal-head { position: relative; height: 34px; background: var(--c-surface-3); border-bottom: 1px solid var(--c-border); pointer-events: none; }
.cal-corner2 { position: absolute; left: 0; top: 0; bottom: 0; background: var(--c-surface-3); border-right: 1px solid var(--c-border); z-index: 2; }
.thr { position: absolute; top: 0; height: 34px; line-height: 34px; font-size: 11px; color: var(--c-text-muted); transform: translateX(-50%); font-family: var(--font-head); font-weight: 600; white-space: nowrap; }
.thr::after { content: ""; position: absolute; left: 50%; bottom: 0; width: 1px; height: 6px; background: var(--c-border-strong); }
.cal-body { position: relative; }
.vline { position: absolute; top: 0; bottom: 0; width: 1px; background: var(--c-border); opacity: .7; pointer-events: none; }
.day-band { position: absolute; left: 0; right: 0; border-bottom: 1px solid var(--c-border); pointer-events: none; }
.day-band.weekend { background: rgba(252,231,243,.4); }
.day-label { position: absolute; left: 0; top: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; background: var(--c-surface-2); border-right: 1px solid var(--c-border); z-index: 2; pointer-events: none; }
.day-label.weekend { background: linear-gradient(180deg, #fef3f9, #fdeef8); }
.day-label .th { font-weight: 700; color: var(--c-text-heading); font-size: 14px; font-family: var(--font-head); }
.day-label.weekend .th { color: var(--c-primary-dark); }
.day-label .en { font-size: 9px; color: var(--c-text-muted); text-transform: uppercase; letter-spacing: .3px; }

/* class block */
.ev { position: absolute; border-radius: 9px; padding: 3px 7px; overflow: hidden; color: #fff; background: var(--ev); box-shadow: var(--shadow-sm); border: 1px solid rgba(255,255,255,.35); cursor: grab; user-select: none; touch-action: none; display: flex; flex-direction: column; justify-content: center; gap: 1px; z-index: 3; transition: box-shadow .16s, filter .16s; }
.ev:hover { box-shadow: var(--shadow-md); filter: brightness(1.05); z-index: 5; }
.ev.dragging { box-shadow: var(--shadow-lg); filter: brightness(1.07); z-index: 9; cursor: grabbing; }
.ev.dirty { box-shadow: 0 0 0 2.5px var(--c-warning), var(--shadow-md); z-index: 6; }
.ev.isnew { box-shadow: 0 0 0 2.5px var(--c-success), var(--shadow-md); z-index: 6; }
.ev-t { font-weight: 700; font-family: var(--font-head); font-size: 12.5px; line-height: 1.1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-shadow: 0 1px 1px rgba(0,0,0,.18); }
.ev-time { font-size: 10px; opacity: .96; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 2px; }
.ev-time .mdi { font-size: 11px; }
.ev.adminonly { background-image: repeating-linear-gradient(45deg, rgba(255,255,255,.16) 0 6px, transparent 6px 12px); }
.ev .adm-badge { position: absolute; top: 2px; right: 3px; }
.ev .adm-badge .mdi { font-size: 12px; }
.rz { position: absolute; top: 0; bottom: 0; width: 11px; cursor: ew-resize; z-index: 4; }
.rz-l { left: 0; } .rz-r { right: 0; }
.rz::after { content: ""; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 3px; height: 40%; background: rgba(255,255,255,.75); border-radius: 2px; opacity: 0; transition: opacity .16s; }
.ev:hover .rz::after { opacity: .85; }

/* dialog extras */
.fhint { font-size: 11.5px; color: var(--c-text-muted); margin-top: 5px; display: flex; align-items: flex-start; gap: 5px; }
.fhint .mdi { font-size: 14px; color: var(--c-info); }
.color-row { display: flex; gap: 8px; flex-wrap: wrap; }
.color-dot { width: 34px; height: 34px; border-radius: 50%; cursor: pointer; border: 3px solid transparent; transition: transform .16s; }
.color-dot:hover { transform: scale(1.1); }
.color-dot.sel { border-color: var(--c-text-heading); transform: scale(1.1); }
.danger-txt { background: var(--c-error-bg); color: var(--c-error-tx); border-radius: 10px; padding: 10px 12px; font-size: 13px; display: flex; gap: 8px; align-items: flex-start; }
.danger-txt .mdi { font-size: 18px; flex: none; }
.foot-left { margin-right: auto; }

/* save bar */
.savebar { position: fixed; left: 50%; bottom: 20px; transform: translateX(-50%); display: flex; align-items: center; gap: 12px; background: var(--c-surface); border: 1px solid var(--c-border-strong); box-shadow: var(--shadow-lg); border-radius: var(--radius-pill); padding: 9px 12px 9px 20px; z-index: 90; max-width: calc(100vw - 32px); }
.savebar .sb-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--c-warning); flex: none; box-shadow: 0 0 0 4px var(--c-warning-bg); }
.savebar .sb-info { font-size: 13.5px; color: var(--c-text-body); white-space: nowrap; }
.savebar .sb-info b { color: var(--c-text-heading); font-family: var(--font-head); }
.savebar .sb-detail { color: var(--c-text-muted); font-size: 12px; }
.sb-slide-enter-active, .sb-slide-leave-active { transition: transform .32s var(--ease), opacity .32s var(--ease); }
.sb-slide-enter-from, .sb-slide-leave-to { transform: translateX(-50%) translateY(150%); opacity: 0; }
@media (max-width: 560px) {
  .savebar { flex-wrap: wrap; border-radius: var(--radius-lg); justify-content: center; }
  .savebar .sb-info { white-space: normal; text-align: center; width: 100%; }
}
</style>
